import { useEffect, useRef, useState } from 'react';
import { ChatListItem } from '../../models/chat';
import { IconChevronD, IconPlus } from '../Icons';

interface ChatSelectorProps {
  chats: ChatListItem[];
  currentChatId: string | null;
  isDraftMode: boolean;
  onSelect: (chatId: string) => void;
  onBeginDraft: () => void;
}

export default function ChatSelector({
  chats,
  currentChatId,
  isDraftMode,
  onSelect,
  onBeginDraft,
}: ChatSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const currentLabel = isDraftMode
    ? 'New chat'
    : chats.find((c) => c.id === currentChatId)?.name ?? 'Select chat';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="btn sm">
        <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {currentLabel}
        </span>
        <IconChevronD size={12} style={{ transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            width: 260,
            zIndex: 30,
            padding: 6,
            maxHeight: 320,
            overflow: 'auto',
            background: 'var(--surface)',
            boxShadow: '0 18px 36px -18px rgba(0,0,0,0.5)',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onBeginDraft();
            }}
            className={'side-link' + (isDraftMode ? ' active' : '')}
            style={{ width: '100%', background: isDraftMode ? undefined : 'transparent', border: 'none' }}
          >
            <span className="ic">
              <IconPlus />
            </span>
            New chat
          </button>
          {chats.length > 0 && <div className="hairline" style={{ margin: '6px 0' }} />}
          {chats.map((chat) => {
            const isCurrent = chat.id === currentChatId && !isDraftMode;
            const isRunning = chat.currentJobId !== null;
            return (
              <button
                key={chat.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelect(chat.id);
                }}
                className={'side-link' + (isCurrent ? ' active' : '')}
                style={{ width: '100%', background: isCurrent ? undefined : 'transparent', border: 'none' }}
              >
                <span
                  className="ic"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: isRunning ? 'var(--accent)' : 'var(--text-6)',
                    display: 'inline-block',
                  }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chat.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
